export class UTCDate implements Date {
  private inner: Date;

  constructor(year: number, month: number, date: number) {
    this.inner = new Date(Date.UTC(year, month, date));
  }

  public static today(date: Date): UTCDate {
    return new UTCDate(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private static daysInMonth(year: number, monthFromOneToTwelve: number): number {
    const result =
      [1, 3, 5, 7, 8, 10, 12].indexOf(monthFromOneToTwelve) >= 0
        ? 31
        : [4, 6, 9, 11].indexOf(monthFromOneToTwelve) >= 0
        ? 30
        : (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
        ? 29
        : 28;
    return result;
  }

  public static iso(value: string | null, roundUp?: boolean): UTCDate | null {
    if (!value) {
      return null;
    }
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }
    const y = +match[1];
    const m = +match[2];
    const d = +match[3];
    if (y > 0 && m > 0 && d > 0 && m >= 1 && m <= 12 && d >= 1 && d <= UTCDate.daysInMonth(y, m)) {
      return new UTCDate(y, m - 1, d);
    }
    if (y > 0 && m > 0 && d === 0 && m >= 1 && m <= 12) {
      return new UTCDate(y, m - 1, roundUp ? UTCDate.daysInMonth(y, m) : 1);
    }
    if (y > 0 && m === 0 && d === 0) {
      return new UTCDate(y, roundUp ? 11 : 0, roundUp ? 31 : 1);
    }
    return null;
  }

  public equals(that: UTCDate): boolean {
    return this.inner.getTime() === that.getTime();
  }

  public toString(): string {
    return this.inner.toUTCString();
  }

  public toDateString(): string {
    var str = this.inner.toUTCString();
    var tokens = str.split(' ');
    var dayOfWeek = tokens[0].replace(',', '');
    var day = tokens[1];
    var month = tokens[2];
    var year = tokens[3];
    return `${dayOfWeek} ${month} ${day} ${year}`;
  }

  public toTimeString(): string {
    return '00:00:00 GMT';
  }

  public toLocaleString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string {
    return this.inner.toLocaleString(locales, options);
  }

  public toLocaleDateString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string {
    return this.inner.toLocaleDateString(locales, options);
  }

  public toLocaleTimeString(locales?: string | string[], options?: Intl.DateTimeFormatOptions): string {
    return this.inner.toLocaleTimeString(locales, options);
  }

  public toUTCString(): string {
    return this.inner.toUTCString();
  }

  public toISOString(): string {
    return this.inner.toISOString();
  }

  public toJSON(key?: any): string {
    return this.inner.toJSON();
  }

  public valueOf(): number {
    return this.inner.valueOf();
  }

  public getTime(): number {
    return this.inner.getTime();
  }

  public getFullYear(): number {
    return this.inner.getUTCFullYear();
  }

  public getUTCFullYear(): number {
    return this.inner.getUTCFullYear();
  }

  public getMonth(): number {
    return this.inner.getUTCMonth();
  }

  public getUTCMonth(): number {
    return this.inner.getUTCMonth();
  }

  public getDate(): number {
    return this.inner.getUTCDate();
  }

  public getUTCDate(): number {
    return this.inner.getUTCDate();
  }

  public getDay(): number {
    return this.inner.getUTCDay();
  }

  public getUTCDay(): number {
    return this.inner.getUTCDay();
  }

  public getHours(): number {
    return this.inner.getUTCHours();
  }

  public getUTCHours(): number {
    return this.inner.getUTCHours();
  }

  public getMinutes(): number {
    return this.inner.getUTCMinutes();
  }

  public getUTCMinutes(): number {
    return this.inner.getUTCMinutes();
  }

  public getSeconds(): number {
    return this.inner.getUTCSeconds();
  }

  public getUTCSeconds(): number {
    return this.inner.getUTCSeconds();
  }

  public getMilliseconds(): number {
    return this.inner.getUTCMilliseconds();
  }

  public getUTCMilliseconds(): number {
    return this.inner.getUTCMilliseconds();
  }

  public getTimezoneOffset(): number {
    return this.inner.getTimezoneOffset();
  }

  public setTime(time: number): number {
    return this.inner.setTime(time);
  }

  public setMilliseconds(ms: number): number {
    return this.setUTCMilliseconds(ms);
  }

  public setUTCMilliseconds(ms: number): number {
    return this.inner.setUTCMilliseconds(ms);
  }

  public setSeconds(sec: number, ms?: number | undefined): number {
    return this.setUTCSeconds(sec, ms);
  }

  public setUTCSeconds(sec: number, ms?: number | undefined): number {
    return this.inner.setUTCSeconds(sec, ms);
  }

  public setMinutes(min: number, sec?: number | undefined, ms?: number | undefined): number {
    return this.setUTCMinutes(min, sec, ms);
  }

  public setUTCMinutes(min: number, sec?: number | undefined, ms?: number | undefined): number {
    return this.inner.setUTCMinutes(min, sec, ms);
  }

  public setHours(hours: number, min?: number | undefined, sec?: number | undefined, ms?: number | undefined): number {
    return this.setUTCHours(hours, min, sec, ms);
  }

  public setUTCHours(hours: number, min?: number | undefined, sec?: number | undefined, ms?: number | undefined): number {
    return this.inner.setUTCHours(hours, min, sec, ms);
  }

  public setDate(date: number): number {
    return this.setUTCDate(date);
  }

  public setUTCDate(date: number): number {
    return this.inner.setUTCDate(date);
  }

  public setMonth(month: number, date?: number | undefined): number {
    return this.setUTCMonth(month, date);
  }

  public setUTCMonth(month: number, date?: number | undefined): number {
    const d = date ? date : this.inner.getUTCDate();
    return this.inner.setUTCMonth(month, d);
  }

  public setFullYear(year: number, month?: number | undefined, date?: number | undefined): number {
    return this.setUTCFullYear(year, month, date);
  }

  public setUTCFullYear(year: number, month?: number | undefined, date?: number | undefined): number {
    const m = month ? month : this.inner.getUTCMonth();
    const d = date ? date : this.inner.getUTCDate();
    return this.inner.setUTCFullYear(year, m, d);
  }

  public addYears(years: number): UTCDate {
    const copy = this.copy();
    copy.setFullYear(this.getFullYear() + years);
    return copy;
  }

  public addMonths(months: number): UTCDate {
    const copy = this.copy();
    copy.setMonth(this.getMonth() + months);
    return copy;
  }

  public addDays(days: number): UTCDate {
    const copy = this.copy();
    copy.setDate(this.getDate() + days);
    return copy;
  }

  public copy(): UTCDate {
    return new UTCDate(this.getFullYear(), this.getMonth(), this.getDate());
  }

  public isWithinNDays(endDate: UTCDate, days: number): boolean {
    const startDateTimestamp = this.getTime();
    const endDateTimestamp = endDate.getTime();
    const timeDifference = endDateTimestamp - startDateTimestamp;
    // Разница в днях
    const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
    // Проверить, не превышает ли разница N дней
    return daysDifference <= days;
  }

  public isBefore(date: UTCDate): boolean {
    const firsttDateTimestamp = this.getTime();
    const secondDateTimestamp = date.getTime();
    return firsttDateTimestamp - secondDateTimestamp < 0;
  }

  [Symbol.toPrimitive](hint: 'default'): string;
  [Symbol.toPrimitive](hint: 'string'): string;
  [Symbol.toPrimitive](hint: 'number'): number;
  [Symbol.toPrimitive](hint: string): string | number {
    return this.inner[Symbol.toPrimitive](hint);
  }
}
